import { default as EChartsReact, default as ReactEcharts } from "echarts-for-react";
import { useEffect, useState } from 'react';


import { t } from 'i18next';
import { DateTime } from 'luxon';
import { DateRange } from 'react-day-picker';
import { hexColor, hslColor, rgbColor } from '../../enums/colors';
import { convertTimezone } from '../../lib/utils';
import { useAppStore } from '../../stores/RootStore';
import { color } from "echarts";
import { TbZoomInArea } from "react-icons/tb";

interface BaseChartProps {
  type: string,
  title: string,
  data: ChartData[], 
  selectedDate: DateRange,
  onChangeDates: Function;
  loading: boolean;
  idleLevel?: number;
}

interface ChartData {
    at: number,
    v: number
}

const EBaseLineChart = ({ data, selectedDate, title, onChangeDates, loading, idleLevel }:BaseChartProps) => {
  const store = useAppStore()
  const [ref, setRef] = useState<EChartsReact | null>();
  const [loaded, setLoaded] = useState(false)
  const [defaultPeriod, setDefaultPeriod] = useState(true);
  const [currentIntervalType, setCurrentIntervalType] = useState<string>('minutes')
  const [zoomed, setZoomed] = useState(false)
  const [reset, setReset] = useState(false)
  const [chartData, setChartData] = useState<any[]>([]);
  const [points, setPoints] = useState({start: 0, end: 100})
  const echartInstance = ref?.getEchartsInstance();

  useEffect(() => {
    if(loading) { setLoaded(false) }
  }, [loading])


  useEffect(() => {
    if (!data) {
      return;
    }
    const itemData: any[] = [];
    data.forEach(it => {
        itemData.push([new Date(it.at + convertTimezone(it.at)), it.v ? (it.v > 0 ? it.v : 0) : null])
    });
    setChartData(itemData);
    setLoaded(true)
  }, [data]);

  useEffect(() => {
    if(zoomed) { 
      onChangeDates(
        {
          from: points.start < 101 ? selectedDate.from! : DateTime.fromMillis(points.start - convertTimezone(points.start)).toUTC().toJSDate(),
          to: points.start < 101 ? selectedDate.to! : DateTime.fromMillis(points.end - convertTimezone(points.end)).toUTC().toJSDate()
        }
      )
    } else {
      onChangeDates(
        {
          from: selectedDate.from!,
          to: selectedDate.to!
        }
      )
    }
  },[zoomed, points])


  const getOptions = () => (
    {
      animation: false,
      title: {
        text: title,
        textStyle: {
          fontSize: 14,
          lineHeight: 36,
          fontWeight: 'bold',
          color: store.theme === "dark" ? hexColor.border : hexColor.borderForeground,
        },
        padding: 0,
        textVerticalAlign: 'auto'
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
          label: {
            // show: true,
            color: store.theme === "dark" ? hexColor.borderForeground : hexColor.border,
            formatter: function (params: { value: number; }) {
              return DateTime.fromMillis(params.value!).toFormat("HH:mm:ss dd.MM.yyyy");
            }
          }
        }
      },
      grid: {
        left: "0px",
        right: "0px",
        bottom: "30px",
        top: "44px",
        backgroundColor: "transparent",
        borderColor: store.theme === "dark" ? hexColor.borderForeground : hexColor.border,
        containLabel: true
      },
      xAxis: [{
        type: 'time',
        boundaryGap: false,
        axisLine: {
          lineStyle: {
            color: store.theme === "dark" ? hexColor.borderForeground : hexColor.border,
            width: 0.5,
          }
        },
        axisLabel: {
          color: store.theme === "dark" ? hexColor.border : hexColor.borderForeground,
          showMinLabel: false,
          showMaxLabel: false,
          alignMaxLabel: 'center',
          alignMinLabel: 'center',
          hideOverlap: false,
        },
      }
      ],
      yAxis: {
        type: 'value',
        offset: 0,
        position: "right",
        axisLabel: {
          inside: true,
          color: store.theme === "dark" ? hexColor.border : hexColor.borderForeground,
        },
        splitLine: {
          lineStyle: {
            color: store.theme === "dark" ? hexColor.borderForeground : hexColor.border,
            type: 'dashed',
            dashOffset: 10
          }
        }
      },
      toolbox: {
        feature: {
          dataZoom: {
            title: {
              zoom: t('zoom'),
              back: zoomed ? t('back') : ""
            },
            icon: {
              zoom: 'path://m4 8v-2c0-1.1.9-2 2-2h2m-4 12v2c0 1.1.9 2 2 2h2m-4-4v2c0 1.1.9 2 2 2h2m8-16h2c1.1 0 2 .9 2 2v2m-4 12h2c1.1 0 2-.9 2-2v-2m-12-5c0 1.7 1.3 3 3 3s3-1.3 3-3-1.3-3-3-3-3 1.3-3 3m8 5-2.5-2.5',
              back: zoomed ? 'path://M3 16m0 1a1 1 0 0 1 1 -1h3a1 1 0 0 1 1 1v3a1 1 0 0 1 -1 1h-3a1 1 0 0 1 -1 -1z M4 12v-6a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-6 M15 13h-4v-4 M11 13l5 -5' : 'blank'
            },
            iconStyle: {
              borderColor: zoomed ? rgbColor.primary : rgbColor.foreground,
              borderWidth: 2
            },
            emphasis: {
              iconStyle: {
                color: 'none',
                borderColor: rgbColor.primary,
                borderWidth: 2,
                textFill: rgbColor.primary
              }
            },
            yAxisIndex: false,
            filterMode: 'empty'
          },
          restore: {
            show: zoomed,
            title: t('reset'),
            iconStyle: {
              borderColor: zoomed ? rgbColor.primary : rgbColor.foreground,
              borderWidth: 2
            },
            emphasis: {
              iconStyle: {
                color: 'none',
                borderColor: rgbColor.primary,
                borderWidth: 2,
                textFill: rgbColor.primary
              }
            },
          },
        }
      },
      series: [
        {
          name: title,
          type: 'line',
          smooth: true,
          symbol: 'none',
          areaStyle: {
            color: rgbColor.primary
          },
          lineStyle: {
            color: rgbColor.primary,
            width: 0.5,
          },
          markLine: idleLevel ? {
            silent: true,
            symbol:["none","none"],
            precision: 0,
            lineStyle: {
              color: store.theme === 'light' ? hexColor.border : hexColor.borderForeground,
            },
            data: [{
              name: t('idle_level'),
              label: {
                color: store.theme === 'light' ? hexColor.border : hexColor.borderForeground,
                show: true,
                formatter: '{b}',
                position: 'insideStartTop'
              },
              yAxis: idleLevel
            }],
            tooltip:{
              trigger:"none"
            }
          } : {},
          data: chartData
        }
      ]
    }
  );
  
  echartInstance?.on("datazoom", "mouseup", function(params: any) {
    if(params.batch[0].startValue && params.batch[0].endValue) {
      setPoints({start: Math.round(params.batch[0].startValue), end: Math.round(params.batch[0].endValue)})
      setZoomed(true)
    } else {
      setPoints({start: params.batch[0].start, end: params.batch[0].end})
      setZoomed(false)
    }
  })

  echartInstance?.on("restore", "click", function(params: any) {
    setZoomed(false)
    onChangeDates(
      {
        from: selectedDate.from!,
        to: selectedDate.to!
      }
    )
  })

  echartInstance?.dispatchAction({
    type: 'takeGlobalCursor',
    key: 'dataZoomSelect',
    // Activate or inactivate.
    dataZoomSelectActive: true
  });

  return ( 
      <div className="flex flex-col space-y-2">
        <ReactEcharts
          option={getOptions()}
          showLoading={!loaded}
          loadingOption={{
            text: '',
            color: hslColor.primary,
            // textColor: store.theme === "dark" ? hslColor.lightBorder : hslColor.darkBorder,
            maskColor: store.theme === "dark" ? hexColor.borderForeground : hexColor.border,
            zlevel: 0,
            // Font size. Available since `v4.8.0`.
            fontSize: 12,
            // Show an animated "spinner" or not. Available since `v4.8.0`.
            showSpinner: true,
            // Radius of the "spinner". Available since `v4.8.0`.
            spinnerRadius: 8,
            // Line width of the "spinner". Available since `v4.8.0`.
            lineWidth: 2,
            // Font thick weight. Available since `v5.0.1`.
            fontWeight: 'normal',
            // Font style. Available since `v5.0.1`.
            fontStyle: 'normal',
            // Font family. Available since `v5.0.1`.
            fontFamily: 'sans-serif'
          }}
          ref={(e) => {
            setRef(e);
          }}
          style={{ width: "100%", height: 200 }}
          opts={{ renderer: "canvas" }}
        />
      </div>
     
  )
};

export default EBaseLineChart;